import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper function to check if user is admin
export const isAdmin = (user) => {
  return user && user.email && user.email.toLowerCase().endsWith('@admin.rajida.com');
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loginTimeoutMinutes, setLoginTimeoutMinutes] = useState(15);
  const navigate = useNavigate();
  const inactivityTimer = useRef(null);

  // Set up axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Fetch login timeout config on login
  useEffect(() => {
    if (token) {
      axios.get('/drive/api/config/get-timeout', { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          if (res.data && res.data.login_timeout_minutes) {
            setLoginTimeoutMinutes(res.data.login_timeout_minutes);
          }
        })
        .catch(() => setLoginTimeoutMinutes(15));
    }
  }, [token]);

  // Inactivity auto-logout logic
  useEffect(() => {
    if (!user || !token) return;
    const timeoutMs = loginTimeoutMinutes * 60 * 1000;
    const resetTimer = () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      inactivityTimer.current = setTimeout(() => {
        // Auto-logout
        setUser(null);
        setToken(null);
        localStorage.removeItem('token');
        navigate('/login');
        alert('You have been logged out due to inactivity.');
      }, timeoutMs);
    };
    // Listen for user activity
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();
    return () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [user, token, loginTimeoutMinutes, navigate]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get('/drive/api/auth/profile');
          setUser(response.data.user);
        } catch (error) {
          console.error('Auth check failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const login = async (username, password) => {
    try {
      const response = await axios.post('/drive/api/auth/login', { username, password });
      const { access_token, user, must_change_password } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(user);
      
      return { success: true, must_change_password };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed' 
      };
    }
  };



  const logout = async () => {
    try {
      await axios.post('/drive/api/auth/logout');
    } catch (e) {
      // Ignore errors, still clear local state
    }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post('/drive/api/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Password change failed' 
      };
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    changePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 